import type { ColumnDef } from '@tanstack/react-table';
import { mcpAccessFor } from '@voll-intelligence/domain';

import type { MemberDetail } from '@web/data/repositories';
import { t } from '@web/i18n';
import { Avatar, AvatarFallback, DataTable, IconButton } from '@web/ui/core';

import { DomainChip } from './domain_chip';
import { McpChip } from './mcp_chip';
import { RoleChip } from './role_chip';
import { StatusChip } from './status_chip';

interface MembersTableProps {
  members: MemberDetail[];
  activeId: string | null;
  onOpen: (userId: string) => void;
}

function buildColumns(): ColumnDef<MemberDetail>[] {
  return [
    {
      id: 'guest',
      header: () => t('members.table.col.guest'),
      cell: ({ row }) => {
        const m = row.original;
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <Avatar size="sm">
              <AvatarFallback displayName={m.name} fallback="·" />
            </Avatar>
            <div className="flex min-w-0 flex-col gap-px">
              <span className="truncate text-meta font-medium leading-tight text-foreground">
                {m.name}
              </span>
              <span className="truncate font-mono text-card-id tracking-tight text-muted-foreground">
                {m.email}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'domain',
      header: () => t('members.table.col.domain'),
      cell: ({ row }) => <DomainChip domain={row.original.domain} />,
    },
    {
      id: 'role',
      header: () => t('members.table.col.role'),
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          <RoleChip role={row.original.role} />
          {mcpAccessFor(row.original.role, row.original.features) ? <McpChip /> : null}
        </div>
      ),
    },
    {
      id: 'status',
      header: () => t('members.table.col.status'),
      cell: ({ row }) => <StatusChip status={row.original.status} />,
    },
    {
      id: 'actions',
      header: () => '',
      cell: () => (
        <div
          className="flex justify-end"
          onClick={(e) => {
            e.stopPropagation();
          }}
          // Wrapper de layout: o `onClick` só barra a propagação pro clique da
          // linha, não é ação do usuário — quem age é o `IconButton` abaixo. Sem
          // o `role`, a regra de click-sem-teclado pede um listener que não tem
          // ação nenhuma pra expor.
          role="presentation"
        >
          <IconButton
            icon="dots-vertical"
            variant="ghost"
            size="sm"
            title={t('members.table.actionsTitle')}
            aria-label={t('members.table.actionsTitle')}
          />
        </div>
      ),
    },
  ];
}

export function MembersTable({
  members,
  activeId,
  onOpen,
}: Readonly<MembersTableProps>): React.JSX.Element {
  const columns = buildColumns();
  return (
    <DataTable
      columns={columns}
      data={members}
      getRowId={(m) => m.userId}
      onRowClick={(m) => {
        onOpen(m.userId);
      }}
      activeRowId={activeId}
      emptyMessage={t('members.table.empty')}
    />
  );
}
